import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  FilledInput,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useCustom } from "@refinedev/core";
import { InView } from "react-intersection-observer";
import ClearIcon from "@mui/icons-material/Clear";
import { SelectMultipleCheckboxDropdown } from "components/inputs/SelectMultipleCheckboxDropdown";
import {
  INVESTMENT_FORMAT_ENUM,
  INVESTMENT_RISK_TYPE_ENUM,
} from "interfaces/investment";
import { get } from "react-hook-form";

export interface Option {
  key: number;
  label: string;
  value: string;
}

export interface PaginationInfo {
  hasNext: boolean;
  nextOffset?: number;
  offset: number;
}

export interface PaginatedEntityList<T> {
  data: T[];
  pagination: PaginationInfo;
}

export interface SearchConfig {
  enabled?: boolean;
  placeholder?: string;
  searchField: string;
}

interface TableColumn<T> {
  field?: keyof T | ((item: T) => React.ReactNode);
  header: string;
  width?: string | number;
  renderCell?: (item: T) => React.ReactNode;
  sortable?: boolean;
  sortField?: string; // API field name for sorting (if different from field)
}

interface InfiniteListProps<T> {
  endpoint: string;
  limit?: number;
  renderItem?: (item: T, index: number) => React.ReactNode;
  emptyMessage?: string;
  gridProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    spacing?: number;
  };
  queryParams?: Record<string, any>;
  searchConfig?: SearchConfig;
  dropDownSearchOption1?: Option[];
  dropDownSearchOption1Title?: string;
  dropDownSearchOption2Title?: string;
  dropDownSearchOption2?: Option[];
  onRefetchTrigger?: boolean | number;
  onRefetchComplete?: () => void;
  setIsLoading?: (loading: boolean) => void;
  tableHeader?: React.ReactNode;
  isTable?: boolean;
  additionalFilters?: Array<{
    field: string;
    operator: string;
    value: any;
  }>;
  columns?: TableColumn<T>[];
  tableProps?: {
    stickyHeader?: boolean;
    headerBgColor?: string;
    responsive?: boolean;
  };
  onDataChange?: (data: T[]) => void;
  resetKey?: string | number; // Optional: resets cache and pagination when changed
  onSearchChange?: (keyword: string) => void; // Optional: notified when the search text changes
  defaultSort?: { field: string; order: "ASC" | "DESC" };
}

export const InfiniteList = <T extends object>({
  endpoint,
  limit = 5,
  renderItem,
  emptyMessage = "No items found.",
  gridProps = { xs: 12, spacing: 2 },
  queryParams = {},
  searchConfig = {
    enabled: false,
    placeholder: "Type keyword or name",
    searchField: "searchTerm",
  },
  dropDownSearchOption1Title,
  dropDownSearchOption2Title,
  dropDownSearchOption1 = [],
  dropDownSearchOption2 = [],
  onRefetchTrigger,
  onRefetchComplete,
  setIsLoading,
  isTable,
  additionalFilters,
  columns,
  tableProps,
  onDataChange,
  resetKey, // Optional prop for cache reset
  defaultSort,
  onSearchChange,
}: InfiniteListProps<T>) => {
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<T[]>([]);
  const [keyword, setKeyword] = useState<string>("");
  const [debouncedKeyword, setDebouncedKeyword] = useState<string>("");
  const [sortField, setSortField] = useState<string | null>(
    defaultSort?.field ?? null
  );
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(
    defaultSort?.order ?? "ASC"
  );
  const isInitialMount = useRef(true);
  const isResettingRef = useRef(false);
  const mergedOffsetsRef = useRef<Set<number>>(new Set());

  const [dropDownSearchOption1Data, setDropDownSearchOption1Data] = useState<
    Option[]
  >([]);
  const [dropDownSearchOption2Data, setDropDownSearchOption2Data] = useState<
    Option[]
  >([]);

  const filtersArray = [];

  // Only add risk types filter if the dropdown is provided
  if (dropDownSearchOption1 && dropDownSearchOption1.length > 0) {
    filtersArray.push({
      field: "filter[riskTypes]",
      operator: "eq",
      value: [
        ...(dropDownSearchOption1Data.map((risk) => risk.value) as [
          INVESTMENT_RISK_TYPE_ENUM
        ]),
        "",
      ],
    });
  }

  // Only add formats filter if the dropdown is provided
  if (dropDownSearchOption2 && dropDownSearchOption2.length > 0) {
    filtersArray.push({
      field: "filter[formats]",
      operator: "eq",
      value: [
        ...(dropDownSearchOption2Data.map((format) => format.value) as [
          INVESTMENT_FORMAT_ENUM
        ]),
        "",
      ],
    });
  }

  // Add any additional filters if provided
  if (additionalFilters) {
    filtersArray.push(...additionalFilters);
  }

  // Build orderBy query parameter
  const orderByParams: Record<string, any> = {};
  if (sortField) {
    orderByParams[`orderBy[${sortField}]`] = sortOrder;
  }

  const {
    data: response,
    isSuccess,
    isFetching: isLoading,
  } = useCustom({
    url: endpoint,
    method: "get",
    config: {
      query: {
        limit,
        offset,
        searchTerm: debouncedKeyword,
        ...orderByParams,
        ...queryParams,
      },
      filters: filtersArray as any,
    },
    // If you change this shape, update useUpdateWithInfiniteListInvalidation invalidation predicates.
    queryOptions: {
      queryKey: resetKey !== undefined
        ? [
            "custom",
            endpoint,
            resetKey,
            offset,
            debouncedKeyword,
            sortField,
            sortOrder,
            JSON.stringify(filtersArray),
            JSON.stringify(queryParams),
          ]
        : [
            "custom",
            endpoint,
            offset,
            debouncedKeyword,
            sortField,
            sortOrder,
            JSON.stringify(filtersArray),
            JSON.stringify(queryParams),
          ],
    },
  });

  // Reset cache and pagination when resetKey changes (optional, backward-compatible)
  useEffect(() => {
    if (resetKey !== undefined) {
      mergedOffsetsRef.current.clear(); // Clear tracked offsets on reset
      isResettingRef.current = true; // Block infinite scroll during reset
      setItems([]);
      setOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Reset items when sort changes (but not on initial mount)
  useEffect(() => {
    // Skip clearing on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Clear items whenever sort changes (including when resetting to null)
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder]);

  // Debounce search to reduce unnecessary API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (keyword !== debouncedKeyword) {
        clear(); // Reset items
        setDebouncedKeyword(keyword);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  useEffect(() => {
    if (!response) return;

    const paginatedData = response.data as PaginatedEntityList<T>;
    const currentOffset = paginatedData.pagination.offset;

    // Same offset again means a refetch (e.g. React Query invalidation after a mutation).
    // The duplicate guard below is only for out-of-order *initial* pagination, not refetches.
    const isRefetchOfSeenOffset = mergedOffsetsRef.current.has(currentOffset);

    if (isRefetchOfSeenOffset) {
      if (currentOffset === 0) {
        mergedOffsetsRef.current = new Set([0]);
        setItems(paginatedData.data);
        onDataChange?.(paginatedData.data);
        isResettingRef.current = false;
      } else {
        clear();
      }
      return;
    }

    mergedOffsetsRef.current.add(currentOffset);

    if (currentOffset === 0) {
      setItems(paginatedData.data);
      onDataChange?.(paginatedData.data); // Notify parent with new data
      isResettingRef.current = false; // Allow infinite scroll now that first page loaded
    } else {
      setItems((prevItems) => {
        const updatedItems = [...prevItems, ...paginatedData.data];
        onDataChange?.(updatedItems); // Notify parent with updated data
        return updatedItems;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (onRefetchTrigger) {
      clear(); // offset = 0 automatically triggers new query due to queryKey change
      onRefetchComplete?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRefetchTrigger]);

  useEffect(() => {
    setIsLoading?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const fetchMoreData = (inView: boolean) => {
    // Block infinite scroll during reset to prevent offset 10 before offset 0
    if (
      !inView ||
      isResettingRef.current ||
      !response?.data?.pagination?.nextOffset
    ) {
      return;
    }

    const nextOffset = response.data.pagination.nextOffset;

    // Do not refetch same offset (idempotent pagination)
    if (mergedOffsetsRef.current.has(nextOffset)) {
      return;
    }

    setOffset(nextOffset);
  };

  const clear = () => {
    setItems([]);
    setOffset(0);
    mergedOffsetsRef.current.clear(); // Clear tracked offsets to allow new search results
    isResettingRef.current = true; // Block infinite scroll during reset
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Cycle through: ASC -> DESC -> back to the list's default ordering
      if (sortOrder === "ASC") {
        setSortOrder("DESC");
      } else if (defaultSort?.field === field) {
        setSortOrder("ASC");
      } else if (sortOrder === "DESC") {
        // Reset to the default ordering, or to no sort when there is none.
        setSortField(defaultSort?.field ?? null);
        setSortOrder(defaultSort?.order ?? "ASC");
      }
    } else {
      // Set new sort field and default to ASC
      setSortField(field);
      setSortOrder("ASC");
    }
    // Note: clear() is called automatically via useEffect when sortField or sortOrder changes
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setKeyword(value);
    onSearchChange?.(value);
  };

  const removeOption = (
    item: Option,
    setValue: React.Dispatch<React.SetStateAction<Option[]>>,
    array: Option[]
  ) => {
    const options = array.filter((selected) => selected.key !== item.key);
    setValue(options);
    clear();
  };

  const reset = () => {
    setKeyword("");
    setDebouncedKeyword("");
    onSearchChange?.("");
    clear();
  };

  return (
    <Box sx={{ marginY: 4 }}>
      {searchConfig.enabled && (
        <>
          <Grid container columnSpacing={1} rowSpacing={1}>
            {dropDownSearchOption1 && dropDownSearchOption1.length > 0 && (
              <Grid item xs={12} md={4}>
                <SelectMultipleCheckboxDropdown
                  setSelected={setDropDownSearchOption1Data}
                  options={dropDownSearchOption1}
                  selected={dropDownSearchOption1Data}
                  title={dropDownSearchOption1Title as string}
                  clear={clear}
                />
              </Grid>
            )}
            {dropDownSearchOption2 && dropDownSearchOption2.length > 0 && (
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth>
                  <SelectMultipleCheckboxDropdown
                    setSelected={setDropDownSearchOption2Data}
                    options={dropDownSearchOption2}
                    selected={dropDownSearchOption2Data}
                    title={dropDownSearchOption2Title as string}
                    clear={clear}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              md={
                !dropDownSearchOption1?.length && !dropDownSearchOption2?.length
                  ? 12
                  : !dropDownSearchOption2?.length ||
                    !dropDownSearchOption1?.length
                  ? 8
                  : 4
              }
            >
              <FormControl variant="outlined" fullWidth>
                <FilledInput
                  className="search"
                  placeholder={searchConfig.placeholder}
                  size="small"
                  id="keyword"
                  name="keyword"
                  onChange={handleSearchChange}
                  value={keyword}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  endAdornment={
                    keyword !== "" && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => reset()} edge="end">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            sx={{ my: 2 }}
            container
            justifyContent={{ xs: "start", md: "flex-wrap" }}
            alignItems="center"
            columns={16}
            spacing={{ xs: 1, md: 0 }}
            rowSpacing={2}
          >
            {((dropDownSearchOption1Data &&
              dropDownSearchOption1Data.length > 0) ||
              (dropDownSearchOption2Data && dropDownSearchOption2Data.length) ||
              keyword) && (
              <Typography
                sx={{ color: "neutral.main" }}
                variant="subtitle3"
                component="span"
              >
                Showing results for
              </Typography>
            )}
            {dropDownSearchOption1Data.map((risk) => (
              <Grid key={risk.key} margin={{ md: 1 }}>
                <FormControl size="small">
                  <OutlinedInput
                    value={risk.label}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            removeOption(
                              risk,
                              setDropDownSearchOption1Data,
                              dropDownSearchOption1Data
                            )
                          }
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            ))}
            {dropDownSearchOption2Data.map((format) => (
              <Grid key={format.key} margin={{ md: 1 }}>
                <FormControl size="small">
                  <OutlinedInput
                    value={format.label}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            removeOption(
                              format,
                              setDropDownSearchOption2Data,
                              dropDownSearchOption2Data
                            )
                          }
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            ))}
            {keyword && (
              <Grid margin={{ md: 1 }}>
                <FormControl size="small">
                  <OutlinedInput
                    value={keyword}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            reset();
                          }}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </>
      )}
      {isSuccess && (
        <>
          {isTable && columns ? (
            <TableContainer component={Paper}>
              <Table stickyHeader={tableProps?.stickyHeader}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: tableProps?.headerBgColor || "grey.100",
                    }}
                  >
                    {columns.map((column, index) => {
                      const fieldName = column.sortField || (typeof column.field === "string" ? column.field : null);
                      const isSortable = column.sortable === true || (column.sortable !== false && fieldName !== null);
                      const isCurrentSort = sortField === fieldName;
                      
                      return (
                        <TableCell
                          key={`header-${index}`}
                          width={column.width}
                          sx={{ 
                            fontWeight: "bold",
                            cursor: isSortable ? "pointer" : "default",
                            userSelect: "none",
                          }}
                          onClick={() => isSortable && fieldName && handleSort(fieldName)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {column.header}
                            {isSortable && (
                              <Box sx={{ display: "flex", flexDirection: "column", ml: 0.5 }}>
                                <ArrowUpwardIcon
                                  sx={{
                                    fontSize: 14,
                                    opacity: isCurrentSort && sortOrder === "ASC" ? 1 : 0.3,
                                    color: isCurrentSort && sortOrder === "ASC" ? "primary.main" : "inherit",
                                  }}
                                />
                                <ArrowDownwardIcon
                                  sx={{
                                    fontSize: 14,
                                    marginTop: -1,
                                    opacity: isCurrentSort && sortOrder === "DESC" ? 1 : 0.3,
                                    color: isCurrentSort && sortOrder === "DESC" ? "primary.main" : "inherit",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items
                    .filter((item) =>
                      renderItem ? renderItem(item, 0) !== null : true
                    )
                    .map((item, rowIndex) => (
                      <TableRow key={`row-${rowIndex}`}>
                        {columns.map((column, colIndex) => (
                          <TableCell key={`cell-${rowIndex}-${colIndex}`}>
                            {column.renderCell
                              ? column.renderCell(item)
                              : typeof column.field === "function"
                              ? column.field(item)
                              : get(item, column.field as string)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={gridProps.spacing}>
              {items

                .filter((item) => renderItem && renderItem(item, 0) !== null)
                .map((item, index) => (
                  <Grid
                    key={`item-${index}`}
                    item
                    xs={gridProps.xs}
                    sm={gridProps.sm}
                    md={gridProps.md}
                    lg={gridProps.lg}
                  >
                    {renderItem && renderItem(item, index)}
                  </Grid>
                ))}
            </Grid>
          )}
        </>
      )}

      {isLoading ? (
        <Grid item xs={12} textAlign="center" sx={{ m: 3 }}>
          <CircularProgress color="primary" />
        </Grid>
      ) : response?.data?.pagination.hasNext ? (
        <InView as="div" onChange={fetchMoreData} />
      ) : null}

      {!isLoading && items.length === 0 && (
        <Typography variant="bodyCopyRegular" p={1}>
          {emptyMessage}
        </Typography>
      )}
    </Box>
  );
};
